// ============================================================================
// infiniwell brand tokens — SINGLE SOURCE OF TRUTH for the theme.
//
// This is the ONLY file in the app that is allowed to contain raw hex/color
// literals. Everything else (the MUI theme, ColorsEnum, charts, components)
// must import from here so the palette is configurable in one place.
//
// Mirror of admin-frontend/src/common/theme/brandTokens.ts — keep in sync.
// Source: /orchestrator/brand-template.html (BRAND REFERENCE v1).
// ============================================================================

/** Convert a #RRGGBB hex to an rgba() string. */
export function hexToRGBA(hex: string, alpha: number): string {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ---- Color ramps -----------------------------------------------------------
export const ramps = {
  primary: {
    50: '#EEF3FA', 100: '#D6E2F1', 200: '#AEC4E2', 300: '#85A5D2', 400: '#5D87C3',
    500: '#3E6DAF', 600: '#35619E', 700: '#2C5082', 800: '#233F66', 900: '#1A2E4A',
  },
  accent: {
    50: '#FBEEE8', 100: '#F5D9CB', 300: '#E5A488', 500: '#D97757', 600: '#C15F3C', 700: '#9A4A2C',
  },
  neutral: {
    0: '#FFFFFF', 25: '#FAF9F5', 50: '#F5F3EC', 100: '#EBE8DF', 200: '#DAD5C8', 300: '#BCB5A6',
    400: '#928B7C', 500: '#6B6459', 600: '#4A453D', 700: '#33302B', 800: '#262421', 850: '#1F1E1B',
    900: '#171614', 950: '#0F0E0C',
  },
} as const;

// ---- Semantic colors (amber lives ONLY as warning) -------------------------
export const semantic = {
  success: { main: '#2E9E5B', dark: '#62C98C', tint: '#E7F3EA' },
  warning: { main: '#E0902A', dark: '#F0B24E', tint: '#FBEFDA' },
  error: { main: '#CB3A3A', dark: '#E97C7C', tint: '#F8E7E7' },
  info: { main: '#3E82C4', dark: '#6FA8DE', tint: '#E7F0FA' },
} as const;

// ---- Role mapping per mode (the variables the theme exposes) ----------------
export const roles = {
  light: {
    // Platform background is pure white (per product direction); cards/paper stay white,
    // chat surfaces use the neutral ramp (see brand template) for separation.
    bg: '#FFFFFF', paper: '#FFFFFF', elevated: '#FFFFFF', divider: '#EBE8DF',
    text: '#1A1815', text2: '#6B6459', text3: '#A69F92',
    primary: '#3E6DAF', primaryHover: '#35619E', onPrimary: '#FFFFFF',
    accent: '#C15F3C', accentBright: '#D97757', onAccent: '#FFFFFF',
  },
  dark: {
    bg: '#171614', paper: '#262421', elevated: '#302E2A', divider: 'rgba(235,232,223,.12)',
    text: '#ECEAE3', text2: '#ADA699', text3: '#6E685D',
    primary: '#5D87C3', primaryHover: '#85A5D2', onPrimary: '#0A1220',
    accent: '#D97757', accentBright: '#E5A488', onAccent: '#2A1005',
  },
} as const;

// Widen values to `string` so both `roles.light` and `roles.dark` (whose `as const`
// literal types differ) are assignable to the shared shape.
export type RoleTokens = { [K in keyof typeof roles.light]: string };
export const rolesFor = (isDark: boolean): RoleTokens => (isDark ? roles.dark : roles.light);

// ---- Chart palette (6 categorical, no amber) -------------------------------
export const chartPalette = ['#3E6DAF', '#D97757', '#2E9CB0', '#8E7CC3', '#2E9E5B', '#6B6459'] as const;

/** Vitals series → brand chart colors (used by demo Recharts). */
export const vitalsColors = {
  SBP: chartPalette[0], // steel blue
  DBP: chartPalette[1], // coral
  HR: chartPalette[2],  // teal
  SpO2: chartPalette[4], // green
} as const;

// ---- Typography ------------------------------------------------------------
export const fonts = {
  brand: '"Infiniwell","Segoe UI",Roboto,Helvetica,Arial,sans-serif',
  ui: '"Inter","Roboto",-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif',
  mono: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
} as const;

// ---- Shape / spacing / elevation -------------------------------------------
export const radius = { default: 8, large: 12, pill: 999 } as const;
export const spacingBase = 8;

export const shadow = {
  light: '0 1px 2px rgba(26,24,21,.05),0 6px 20px rgba(26,24,21,.05)',
  dark: '0 1px 2px rgba(0,0,0,.4),0 10px 30px rgba(0,0,0,.5)',
} as const;
export const shadowFor = (isDark: boolean): string => (isDark ? shadow.dark : shadow.light);

// Dialogs/modals sit above everything — a stronger, distinct elevation than cards.
export const dialogShadow = {
  light: '0 12px 40px rgba(26,24,21,.22)',
  dark: '0 16px 48px rgba(0,0,0,.6)',
} as const;
export const dialogShadowFor = (isDark: boolean): string => (isDark ? dialogShadow.dark : dialogShadow.light);

// Modal scrim/backdrop: warm ink @42% (light) · black @60% (dark).
export const scrim = {
  light: 'rgba(23,22,20,.42)',
  dark: 'rgba(0,0,0,.6)',
} as const;
export const scrimFor = (isDark: boolean): string => (isDark ? scrim.dark : scrim.light);

// ---- Logo (raster asset used as-is; never recolored) -----------------------
export const logo = { blue: '#406CA9', ink: '#040707', darkModeInk: '#ECEAE3' } as const;
